import type { FiskalReceiptPrinted } from '@agos/api-client/model'
import { apiErrorMessage } from '@agos/api-client'
import { parseMoneyToCents } from '@agos/shared'
import { postFiskalReceipt, useGetFiskalRegisters } from '@agos/api-client/fiskal'
import { postCouponsValidate } from '@agos/api-client/menu'
import { postOrdersSplitPayment } from '@agos/api-client/orders'
import { useGetEmployeesWaiters } from '@agos/api-client/hr'
// §9.148 — *na sekeru* at the moment they ask for it, which is here rather than on the tabs screen.
import {
  useGetOrderTabs,
  usePostOrderTabs,
  usePostOrderTabsIdOrdersOrderId,
} from '@agos/api-client/orders'
import { useGetSalesChannels } from '@agos/api-client/orders'
import {
  payWaiterProblem,
  paymentNeedsWaiter,
  waiterOptions,
} from '../order-builder/waiter-choice'
import type { OrderListItem } from '@agos/api-client/model'
import {
  T,
  alreadyPrinted,
  alreadyPrintedNote,
  centsToEuros,
  formatCents,
  lineTotalCents,
  openTabChoices,
  printVirtualReceipt,
  registerIds,
  registerName,
  naSekeruEnabled,
  tabRefusal,
  tt,
} from '@agos/shared'
import { cn, toast } from '@agos/ui'
import { Banknote, CreditCard, Loader2, Minus, NotebookPen, Plus, Receipt, Split, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import {
  NO_DISCOUNT,
  couponPayload,
  closeDiscountPayload,
  discountForClose,
  discountNote,
  discountProblems,
  payableCents,
  type DiscountMode,
  type PayDiscount,
} from './pay-discount'
import {
  alreadyPaid,
  alreadyPaidNote,
  closeTableQuestion,
  hasSomethingToPay,
  isTransientKasaWarning,
  lineKey,
  receiptLines,
  splitSelection,
  wholeBill,
  type PayableLine,
} from './split-payment'

/**
 * Settling a table.
 *
 * Two ways out: the whole bill at once, or a split where a leaving guest pays
 * for the lines they had. The split closes the original order and re-issues it
 * as a paid one and a still-open one, and prints a receipt for the paid half
 * only — `splitSelection` decides the division and is tested separately,
 * because a quantity on the wrong side either charges somebody twice or lets a
 * table walk.
 *
 * Ported from legacy `pages/restaurant.tsx`.
 */

const PAYMENT_METHODS = [
  { value: 'cash', label: tt('Hotovosť'), icon: Banknote },
  { value: 'card_terminal', label: tt('Karta – terminál'), icon: CreditCard },
] as const

/** Every eight seconds, so a table cancelled in the kitchen is noticed. */
export const PAYMENT_POLL_MS = 8000

/**
 * §11.4 — in virtual mode the register never printed anything, so the browser
 * does. Legacy only raised a toast here, which left the customer with no receipt.
 *
 * Outside the component on purpose: it reads the clock, and a function that reads
 * the clock has no business being defined in a render body.
 */
function printIfVirtual(response: FiskalReceiptPrinted): void {
  if (response.virtualReceipt !== true || !response.receipt) return
  const printed = printVirtualReceipt(response.receipt, Date.now())
  if (printed) return
  toast({
    variant: 'destructive',
    title: tt('Doklad sa nepodarilo otvoriť'),
    description: tt('Povoľte vyskakovacie okná a doklad vytlačte znova.'),
  })
}

/** The names the port ships with, drawn until the franchise types one over a register — §10A.35. */
const SHIPPED_REGISTER_NAMES = { restauracia: tt('Kasa Reštaurácia'), rozvoz: tt('Kasa Rozvoz') }

export function PaymentDialog({
  order,
  onClose,
  onPaid,
}: {
  order: OrderListItem
  onClose: () => void
  onPaid: () => void
}) {
  const [method, setMethod] = useState<string>('cash')
  // §9.128 — who is taking the money. Asked here and not only at order time, because on a shared
  // till the person who opened the table is routinely not the person settling it.
  const [payWaiterId, setPayWaiterId] = useState<number | null>(null)
  const { data: waiterData, isSuccess: waitersLoaded } = useGetEmployeesWaiters({
    query: { enabled: paymentNeedsWaiter(order.type) },
  })
  const waiters = useMemo(() => waiterOptions(waiterData), [waiterData])

  /**
   * §9.320, owner Q5 - *"čašník to be preselected that one, who was assigned."*
   *
   * The order carries `employeeId`, and nothing read it: §9.128 made naming a waiter mandatory and
   * then asked for a name the order already had. **Preselected, not forced** - the select stays, and
   * the person who took the order is routinely not the person settling it, which is why §9.128 asks
   * at all.
   *
   * **Only when that employee is on today's shift list.** `/employees/waiters` answers who is, and
   * preselecting somebody the server would refuse turns a red asterisk into a failed save. Runs once
   * per order and shift list, and never over a choice somebody has made: `payWaiterId === null` is
   * the guard, so re-picking is never undone by a refetch.
   */
  useEffect(() => {
    if (payWaiterId !== null) return
    const assigned = order.employeeId
    if (assigned === undefined || assigned === null) return
    if (!waiters.some((waiter) => waiter.id === assigned)) return
    setPayWaiterId(assigned)
  }, [order.employeeId, waiters, payWaiterId])
  /**
   * §9.148 — the owner's point 42b. Nobody says *put it on my tab* at the door; they say it when the
   * bill arrives, which is this dialog.
   */
  const [tabPanelOpen, setTabPanelOpen] = useState(false)
  const [chosenTabId, setChosenTabId] = useState<number | null>(null)
  const [newTabName, setNewTabName] = useState('')
  const { data: tabData } = useGetOrderTabs({ query: { enabled: tabPanelOpen } })
  const tabs = useMemo(() => openTabChoices(tabData), [tabData])
  const { data: channelData } = useGetSalesChannels()
  const channel = useMemo(
    () =>
      (Array.isArray(channelData) ? channelData : []).find(
        (one) => (one as { slug?: string }).slug === order.type,
      ) as
        | { name?: string; allowsDeferredPayment?: boolean; vatSlot?: string | null; onPremise?: boolean }
        | undefined,
    [channelData, order.type],
  )
  // The gate the server enforces in `OrderTabService.append`. Said on the screen rather than
  // discovered as a rejected request — OWNER-QUESTIONS §9 is the switch that opens it.
  /* §9.281, owner O20b — the slate is refused outright when its own channel is switched off. That is
   * a different question from whether *this* order's channel permits a deferred payment: `restaurant`
   * does, which is why the button showed while the feature was off. */
  // §9.299 - the owner asked twice for this to be *hidden* when the channel is off, not disabled with
  // a reason. The two refusals are different messages: "not for this order" is true while the slate
  // runs, and "not here at all" is what a franchise that does not run one should see - which is
  // nothing. Read from the channel rather than by matching `tabRefusal`'s sentence, which would break
  // the first time one is reworded.
  const slateOffered = naSekeruEnabled(Array.isArray(channelData) ? channelData : undefined)
  const tabBlock = tabRefusal(
    channel?.name,
    channel?.allowsDeferredPayment === true,
    slateOffered,
  )

  const [isSplitting, setIsSplitting] = useState(false)
  /**
   * How many of each line the leaving guest is paying for, keyed by `order_items.id` - §9.408.
   *
   * **Keyed by the row, not by its position in `order.items`.** That array is refetched every eight
   * seconds by the board underneath this dialog, so a position-keyed tick moves to whatever lands at
   * that index - which is what the restaurant reported: *"kým sa terminál rozsvietil, toto sa
   * označilo samé"*.
   */
  const [selected, setSelected] = useState<Map<number, number>>(new Map())
  const [isWorking, setIsWorking] = useState(false)
  /**
   * §9.366, FB-654 - the close is confirmed before it happens.
   *
   * The owner reported a table closing on one click with the card never charged and the receipt
   * never printed, because `payAll` closes first and asks the terminal afterwards. `'all'` and
   * `'split'` rather than a boolean, so the dialog can name which of the two is about to run.
   */
  /**
   * §9.367 - the question after the payment, and what it was paid.
   *
   * `printed` is carried so the dialog can say the document did not come out without hiding the
   * close: a cash payment may still want the table closed.
   */
  const [askingToClose, setAskingToClose] = useState<{ paidCents: number; printed: boolean } | null>(
    null,
  )

  const openTab = usePostOrderTabs()
  const appendToTab = usePostOrderTabsIdOrdersOrderId()

  /**
   * Move the bill to the slate. **Not a payment** — no money is taken, no receipt is printed, and
   * the fiscal document belongs to settlement (§10A.1). The order keeps its channel: a meal eaten at
   * table 4 was sold by the restaurant, and the tab only records that it is not paid for yet.
   */
  const sendToTab = async () => {
    if (tabBlock !== null || isWorking) return
    setIsWorking(true)
    try {
      let tabId = chosenTabId
      if (tabId === null) {
        const name = newTabName.trim()
        if (name === '') {
          toast({ variant: 'destructive', title: tt('Zadajte meno účtu') })
          return
        }
        const opened = (await openTab.mutateAsync({ data: { name } })) as { id?: number }
        if (typeof opened.id !== 'number') {
          toast({ variant: 'destructive', title: tt('Účet sa nepodarilo otvoriť') })
          return
        }
        tabId = opened.id
      }
      await appendToTab.mutateAsync({ id: tabId, orderId: order.id ?? 0 })
      toast({
        title: tt('Presunuté na sekeru'),
        description: tt('Stôl je voľný. Účet sa uhrádza na obrazovke Účty na sekeru.'),
      })
      onPaid()
    } catch (error) {
      toast({
        variant: 'destructive',
        title: tt('Nepodarilo sa presunúť na sekeru'),
        // The server's own words when it has any — `append` refuses with the channel named.
        description: apiErrorMessage(error, tt('Server objednávku odmietol.')),
      })
    } finally {
      setIsWorking(false)
    }
  }
  const [discount, setDiscount] = useState<PayDiscount>(NO_DISCOUNT)
  // §10A.17 — the owner's *"also that you can choose when printing"*. Empty means *do not send one*,
  // which leaves the server to use the channel's configured register, then its capability rule.
  // Deliberately not preselected to what the rule would pick: showing a guess as a choice makes a
  // person confirm something nobody decided.
  const [kasaId, setKasaId] = useState<string>('')
  // §10A.35 — the registers this franchise has, not the two the port shipped with. Empty means
  // *podľa kanála*, which stays the default: the channel's own register is nearly always right.
  const { data: registerData } = useGetFiskalRegisters()
  const registerOptions = useMemo(() => {
    const rows = Array.isArray(registerData)
      ? (registerData as Array<{ kasaId?: string; label?: string }>)
      : []
    return registerIds(registerData).map((id) => ({
      id,
      label: registerName(id, rows, SHIPPED_REGISTER_NAMES),
    }))
  }, [registerData])
  const [couponBusy, setCouponBusy] = useState(false)

  const lines = (order.items ?? []) as PayableLine[]
  const split = splitSelection(lines, selected)
  const dueCents = isSplitting
    ? split.paidCents
    : lines.reduce((total, line) => total + lineTotalCents(line.unitPrice, line.quantity ?? 0), 0)

  const setQuantity = (index: number, delta: number) => {
    setSelected((current) => {
      const next = new Map(current)
      const line = lines[index]
      const limit = line?.quantity ?? 0
      const key = lineKey(line, index)
      const value = Math.max(0, Math.min(limit, (current.get(key) ?? 0) + delta))
      if (value === 0) next.delete(key)
      else next.set(key, value)
      return next
    })
  }

  /** The register's own warnings, minus the ones that mean "reconnecting". */
  const reportKasa = (response: FiskalReceiptPrinted) => {
    const warning = response.warning
    if (warning && !isTransientKasaWarning(warning)) {
      toast({
        title: tt('Upozornenie kasy'),
        description: typeof warning === 'string' ? warning : JSON.stringify(warning),
      })
    }
    if (response.paperOut === true) {
      toast({
        variant: 'destructive',
        title: tt('Papier chýba v kase'),
        description: tt('Doklad bol uzavretý – dotlačí sa automaticky po vložení papiera.'),
      })
    }
  }

  /**
   * §9.367 - it says whether it printed.
   *
   * It swallowed every failure into a toast and returned nothing, which is what let `payAll` close
   * the table over a document that never came out - the owner's *"bloček also did not printed and it
   * was closed"*.
   */
  const printReceipt = async (
    target: { id?: number; orderNumber?: string; type?: string; total?: number; discount?: number },
    items: PayableLine[],
  ): Promise<boolean> => {
    try {
      const response = await postFiskalReceipt({
        orderId: target.id ?? 0,
        orderNumber: target.orderNumber ?? '',
        orderType: target.type,
        // Only sent when somebody picked one. An empty string would read as a register named "".
        ...(kasaId === '' ? {} : { kasaId }),
        paymentMethod: method,
        total: target.total ?? 0,
        // The receipt carries the discount that was actually given, so the printed document and the
        // order agree about it.
        ...(discountForClose(discount, dueCents) > 0
          ? { discount: discountForClose(discount, dueCents) }
          : target.discount
            ? { discount: target.discount }
            : {}),
        // §9.390 - the channel decides how the food is taxed, not the three names.
        items: receiptLines(items, target.type, channel),
      })

      printIfVirtual(response)
      reportKasa(response)
      return true
    } catch (caught) {
      /**
       * **A receipt that already exists is not a failure to print one** - §9.362, FB-649.
       *
       * `FiskalReceiptService` answers 409 `ALREADY_PRINTED` with the document's number in the
       * sentence, and this said *"Pokladňa - chyba"* in red over it. The order is closed either way
       * - the line below is why - so there is nothing for the operator to do except know that the
       * document is out, which is what the server said.
       *
       * The path that produces this without a genuine duplicate is a first attempt whose answer was
       * lost: the owner's 2026-09-18 incident, where the kasa printed and the connection did not
       * survive long enough to say so.
       */
      if (alreadyPrinted(caught)) {
        toast({
          title: tt('Doklad už bol vytlačený'),
          description: alreadyPrintedNote(caught),
        })
        // The document exists, which is what the caller needs to know - §9.362.
        return true
      }
      // The order is already closed; a failed print does not un-sell the food.
      toast({
        variant: 'destructive',
        title: tt('Pokladňa - chyba'),
        description: apiErrorMessage(caught, tt('Nepodarilo sa vytlačiť doklad.')),
      })
      return false
    }
  }

  const paySplit = async () => {
    if (!hasSomethingToPay(selected)) return
    setIsWorking(true)
    try {
      const response = (await postOrdersSplitPayment({
        originalOrderId: order.id ?? 0,
        paymentMethod: method,
        paidItems: split.paidItems,
        remainingItems: split.remainingItems,
        // §9.394 - the discount the receipt is about to print. Without it the register printed one
        // total and the order closed at another. **`dueCents`, not `split.paidCents`**: the two are
        // equal while splitting and `dueCents` is the expression the receipt uses, so the base
        // cannot drift between the document and the order.
        ...closeDiscountPayload(discount, dueCents),
      })) as Record<string, unknown>

      const paid = response.paidOrder as
        | { id?: number; orderNumber?: string; type?: string; total?: number; items?: PayableLine[] }
        | undefined
      if (paid) await printReceipt(paid, paid.items ?? split.paidItems)

      toast({
        title: tt('Zaplatené'),
        description: `${formatCents(split.paidCents)} · ${
          response.hasRemaining === true ? tt('stôl zostáva otvorený') : tt('stôl uzavretý')
        }`,
      })
      onPaid()
      onClose()
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, tt('Nepodarilo sa spracovať platbu.')),
      })
    } finally {
      setIsWorking(false)
    }
  }

  /** The whole bill is a split where everything is selected. */
  const needsWaiter = paymentNeedsWaiter(order.type) && !isSplitting
  const waiterBlock = payWaiterProblem(needsWaiter, waitersLoaded, waiters, payWaiterId)

  /**
   * §9.367 - print, then ask about the table.
   *
   * The owner: *"after payment it will ask you 'Chcete zatvoriť stôl?' like in old system"*, and
   * *"make sure you can pay the order in reštaurácia without zatvoriť stôl, so it will be visible in
   * kuchyňa"*. So the document goes out first, and the close is a second decision that may be
   * answered *no* - which leaves the order where the kitchen can still see it.
   *
   * The receipt payload no longer needs the close's answer: `paidOrder ?? order` fell back to the
   * order itself for a whole bill, so this is a reordering rather than a restructure.
   */
  const payAll = async () => {
    setSelected(wholeBill(lines))
    setIsSplitting(false)
    setIsWorking(true)
    try {
      const everything = splitSelection(lines, wholeBill(lines))
      const printed = await printReceipt(
        { ...order, total: centsToEuros(everything.paidCents) },
        lines,
      )
      // The question, whatever the printer said - and it says so when the document did not come out,
      // because a cash payment may still want the table closed.
      setAskingToClose({ paidCents: everything.paidCents, printed })
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, tt('Nepodarilo sa spracovať platbu.')),
      })
    } finally {
      setIsWorking(false)
    }
  }

  /**
   * *Áno, zatvoriť stôl* - §9.367.
   *
   * **The same call `payAll` used to make, with the same payload.** Only when it runs changed, and
   * whether it runs at all: answering *no* leaves the table open and the order in the kitchen.
   */
  const closeTheTable = async (paidCents: number) => {
    setIsWorking(true)
    try {
      const everything = splitSelection(lines, wholeBill(lines))
      await postOrdersSplitPayment({
        originalOrderId: order.id ?? 0,
        paymentMethod: method,
        paidItems: everything.paidItems,
        remainingItems: [],
        ...(payWaiterId === null ? {} : { employeeId: payWaiterId }),
        // §9.394 - the whole-bill path, which is most of them.
        ...closeDiscountPayload(discount, dueCents),
      })
      toast({ title: tt('Zaplatené'), description: formatCents(paidCents) })
      setAskingToClose(null)
      onPaid()
      onClose()
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, tt('Nepodarilo sa uzatvoriť stôl.')),
      })
    } finally {
      setIsWorking(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl w-full max-w-md max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border flex-shrink-0">
          <Receipt className="w-5 h-5 text-primary" />
          <div className="flex-1 min-w-0">
            <p className="font-bold">
              <T>Zaplatiť</T>
            </p>
            <p className="text-xs text-muted-foreground truncate">
              #{order.orderNumber} · {order.customerName}
            </p>
            {/* §9.54 - the note, where the food is handed over. *Faktúru na firmu* is for whoever
              * settles it, and this is the screen that settles it. */}
            {order.notes && (
              <p className="text-[11px] text-amber-600 mt-0.5" data-order-note>
                📝 {order.notes}
              </p>
            )}
          </div>
          <button
            type="button"
            aria-label={tt('Zatvoriť')}
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {order.status === 'cancelled' && (
          <p className="px-5 py-2 bg-red-500/10 border-b border-red-500/30 text-xs font-bold text-red-500">
            <T>Objednávka bola zrušená – nie je možné ju uzatvoriť.</T>
          </p>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <button
            type="button"
            onClick={() => {
              setIsSplitting((splitting) => !splitting)
              setSelected(new Map())
            }}
            className={cn(
              'w-full flex items-center justify-center gap-2 py-2 rounded-xl border text-xs font-bold transition-colors',
              isSplitting
                ? 'bg-primary/10 border-primary text-primary'
                : 'border-border text-muted-foreground hover:text-foreground',
            )}
          >
            <Split className="w-3.5 h-3.5" /> <T>Rozdeliť účet</T>
          </button>

          <div className="space-y-1.5" data-pay-lines>
            {lines.map((line, index) => {
              const chosen = selected.get(lineKey(line, index)) ?? 0
              return (
                <div
                  // §9.408 - the row's own id, so React keeps a line's DOM node with the line rather
                  // than with the slot it happened to occupy when the board last refreshed.
                  key={lineKey(line, index)}
                  // Published so the identity is observable from outside: a spec, or somebody
                  // reading the DOM over a waiter's shoulder, can see which row a tick belongs to
                  // rather than inferring it from the order lines happen to be drawn in.
                  data-pay-line={lineKey(line, index)}
                  data-pay-line-chosen={chosen}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl bg-muted/50"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      <T>{line.productName ?? line.customName ?? ''}</T>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {line.quantity}× {formatCents(0 + Number(line.unitPrice) * 100)}
                    </p>
                  </div>
                  {isSplitting ? (
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        aria-label={`${tt('Menej')} ${line.productName ?? ''}`}
                        onClick={() => setQuantity(index, -1)}
                        className="w-6 h-6 rounded-lg bg-background border border-border flex items-center justify-center"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-5 text-center text-sm font-bold tabular-nums">
                        {chosen}
                      </span>
                      <button
                        type="button"
                        aria-label={`${tt('Viac')} ${line.productName ?? ''}`}
                        onClick={() => setQuantity(index, 1)}
                        className="w-6 h-6 rounded-lg bg-background border border-border flex items-center justify-center"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <span className="text-sm font-bold tabular-nums">
                      {formatCents(lineTotalCents(line.unitPrice, line.quantity ?? 0))}
                    </span>
                  )}
                </div>
              )
            })}
          </div>


        </div>

        <div className="border-t border-border px-5 py-4 space-y-3 flex-shrink-0">
          <div className="space-y-2 px-4 pb-2" data-pay-discount>
        <div className="flex gap-2">
          <select
            value={discount.mode}
            onChange={(event) =>
              setDiscount((current) => ({ ...current, mode: event.target.value as DiscountMode }))
            }
            aria-label={tt('Typ zľavy')}
            data-discount-mode
            className="min-h-[40px] rounded-xl bg-card border border-border px-2 text-sm"
          >
            <option value="percent">%</option>
            <option value="fixed">€</option>
          </select>
          <input
            value={discount.value}
            onChange={(event) => setDiscount((current) => ({ ...current, value: event.target.value }))}
            inputMode="decimal"
            placeholder={tt('Zľava')}
            aria-label={tt('Zľava')}
            data-discount-value
            className="flex-1 min-h-[40px] rounded-xl bg-card border border-border px-3 text-sm tabular-nums"
          />
        </div>
        {/* §9.47 - the coupon half of `PayDiscount`, which has existed since it was written and was
          * offered nowhere. The server decides: `/coupons/validate` knows what has expired, been used
          * up or does not reach its minimum, and its refusal is printed as it comes. */}
        <div className="flex gap-2">
          <input
            value={discount.couponCode}
            onChange={(event) =>
              // Typing again clears the previous answer: a code that has been edited has not been
              // validated, and leaving the old discount on screen would apply it to a different code.
              setDiscount((current) => ({
                ...current,
                couponCode: event.target.value,
                couponCents: 0,
                couponError: null,
              }))
            }
            placeholder={tt('Kód kupóna')}
            aria-label={tt('Kód kupóna')}
            data-pay-coupon
            className="flex-1 min-h-[40px] rounded-xl bg-card border border-border px-3 text-sm uppercase"
          />
          <button
            type="button"
            data-pay-coupon-apply
            disabled={discount.couponCode.trim() === '' || couponBusy}
            onClick={() => {
              void (async () => {
                setCouponBusy(true)
                try {
                  // The same two fields `NewOrderPage` reads: a `valid` flag and `discountAmount`.
                  // Read from the payload rather than guessed — the first draft of this looked for a
                  // `discount` field, which the endpoint does not answer, so a good coupon silently
                  // took nothing off.
                  const answer = await postCouponsValidate(couponPayload(discount, dueCents))
                  if (answer.valid !== true) {
                    setDiscount((current) => ({
                      ...current,
                      couponCents: 0,
                      couponError: tt('Kupón neplatí pre túto objednávku'),
                    }))
                    return
                  }
                  setDiscount((current) => ({
                    ...current,
                    couponCents: parseMoneyToCents(answer.discountAmount),
                    couponError: null,
                  }))
                } catch (caught) {
                  // The backend's own Slovak — "Kupón neexistuje", "Kupón vypršal" — not a guess.
                  setDiscount((current) => ({
                    ...current,
                    couponCents: 0,
                    couponError: apiErrorMessage(caught, tt('Neplatný kupón')),
                  }))
                } finally {
                  setCouponBusy(false)
                }
              })()
            }}
            className="min-h-[40px] px-4 rounded-xl bg-card border border-border text-sm font-bold disabled:opacity-50"
          >
            <T>OK</T>
          </button>
        </div>
        {discount.couponError !== null && (
          <p className="text-[11px] text-destructive" data-pay-coupon-error>
            {discount.couponError}
          </p>
        )}

        {discountProblems(discount, dueCents).map((problem) => (
          <p key={problem} className="text-[11px] text-destructive" data-discount-problem>
            {problem}
          </p>
        ))}
        <p className="text-[11px] text-muted-foreground" data-discount-note>
          {discountNote(discount, dueCents)}
        </p>
      </div>

      <div className="flex items-center justify-between font-bold">
            <span>
              <T>Na zaplatenie</T>
            </span>
            <span className="text-xl text-primary tabular-nums" data-pay-due>
              {formatCents(payableCents(discount, dueCents))}
            </span>
          </div>
          {needsWaiter && (
            <div className="mb-2" data-pay-waiter-field>
              <p
                className={cn(
                  'text-[10px] font-bold uppercase tracking-wider mb-1.5 flex items-center gap-1',
                  payWaiterId === null ? 'text-red-500' : 'text-muted-foreground',
                )}
              >
                <T>Čašník / obsluha</T>
                {payWaiterId === null && <span>*</span>}
              </p>
              {waiters.length === 0 && waitersLoaded ? (
                // An empty dropdown reads as a broken screen; the fix is in attendance.
                <p
                  className="text-xs font-semibold text-red-500 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2"
                  data-pay-waiter-none
                >
                  {waiterBlock}
                </p>
              ) : (
                <select
                  value={payWaiterId === null ? '' : String(payWaiterId)}
                  onChange={(event) =>
                    setPayWaiterId(event.target.value === '' ? null : Number(event.target.value))
                  }
                  aria-label={tt('Čašník / obsluha')}
                  data-pay-waiter
                  className={cn(
                    'w-full px-3 py-2.5 rounded-xl bg-background border text-sm font-semibold outline-none',
                    payWaiterId === null ? 'border-red-500/60 text-red-500' : 'border-border',
                  )}
                >
                  <option value="">{tt('- vybrať čašníka -')}</option>
                  {waiters.map((waiter) => (
                    <option key={waiter.id} value={waiter.id}>
                      {waiter.name}
                    </option>
                  ))}
                </select>
              )}
              {waiterBlock !== null && waiters.length > 0 && (
                <p className="text-[10px] text-red-500 mt-1 font-semibold" data-pay-waiter-block>
                  {waiterBlock}
                </p>
              )}
            </div>
          )}
          {/* §9.148 - the third door, beside cash and card.
            *
            * **§9.299 reversed this entry's own prediction.** It said the button should be drawn even
            * when the channel forbids it, *"because an option that vanishes cannot be told apart from
            * one that was never built — which is exactly the report that would come back"*. The
            * report that came back was the opposite, twice: *"Na sekeru is still visible there in that
            * «Zaplatiť» menu, when kanal is disabled, hide it also from there."*
            *
            * So the two refusals are told apart. A channel that is **off** hides the door; every other
            * refusal still draws it disabled with its reason, because *"not for this order"* is worth
            * saying and *"not in this restaurant"* is not. */}
          {!isSplitting && slateOffered && (
            <div className="mb-2" data-pay-tab-section>
              {tabPanelOpen ? (
                <div className="rounded-xl border border-border bg-background p-3 space-y-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    <T>Na ktorý účet</T>
                  </p>
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      data-tab-choice={tab.id}
                      onClick={() => setChosenTabId(tab.id)}
                      className={cn(
                        'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm',
                        chosenTabId === tab.id
                          ? 'bg-primary/10 border-primary text-primary'
                          : 'bg-card border-border',
                      )}
                    >
                      <span className="truncate font-semibold">{tab.name}</span>
                      <span className="tabular-nums text-xs">{formatCents(tab.outstandingCents)}</span>
                    </button>
                  ))}
                  <div className="flex items-center gap-2">
                    <input
                      value={newTabName}
                      onChange={(event) => {
                        setNewTabName(event.target.value)
                        setChosenTabId(null)
                      }}
                      placeholder={tt('… alebo nový účet na meno')}
                      aria-label={tt('Nový účet na meno')}
                      data-new-tab-name
                      className="flex-1 min-h-[38px] rounded-lg bg-card border border-border px-3 text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => void sendToTab()}
                    disabled={isWorking || (chosenTabId === null && newTabName.trim() === '')}
                    data-confirm-tab
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-amber-500 text-white font-bold text-sm disabled:opacity-50"
                  >
                    {isWorking ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <NotebookPen className="w-4 h-4" />
                    )}
                    <T>Presunúť na sekeru</T>
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setTabPanelOpen(true)}
                  disabled={tabBlock !== null || order.status === 'cancelled'}
                  data-pay-to-tab
                  title={tabBlock ?? undefined}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-amber-500/40 text-amber-500 font-bold text-sm disabled:opacity-50"
                >
                  <NotebookPen className="w-4 h-4" /> <T>Na sekeru</T>
                </button>
              )}
              {/* §9.299 - the note goes with the button. A reason for a control nobody can see is
                * worse than silence. */}
              {slateOffered && tabBlock !== null && (
                <p className="text-[10px] text-muted-foreground mt-1" data-pay-tab-block>
                  {tabBlock}
                </p>
              )}
            </div>
          )}

          {/* §9.320, owner Q5 - *"platba hotovosť karta to be at the bottom before zaplatiť
            * všetko… KASA change keep there, but add it there, more compact"*.
            *
            * **This is not a reversal of §9.203.** That entry moved the method row *above* the bill
            * because below it a table of twenty pushed it off the screen. This footer is
            * `flex-shrink-0`, so a long bill cannot push it anywhere - which is the same requirement
            * met better, and it puts the decision beside the button that acts on it.
            *
            * Compact: one row of two, not two full-width buttons, and the register's paragraph of
            * hint text becomes the select's own first option - *podľa kanála* is what it explained. */}
          <div className="grid grid-cols-2 gap-2" data-pay-methods>
            {PAYMENT_METHODS.map((option) => (
              <button
                key={option.value}
                type="button"
                data-pay-method={option.value}
                onClick={() => setMethod(option.value)}
                className={cn(
                  'flex items-center justify-center gap-1.5 px-2 py-2 rounded-xl text-sm font-semibold border transition-all',
                  method === option.value
                    ? 'bg-primary/10 border-primary text-primary'
                    : 'bg-background border-border text-muted-foreground hover:text-foreground',
                )}
              >
                <option.icon className="w-4 h-4 shrink-0" />
                {option.label}
              </button>
            ))}
          </div>

          {/* §10A.17 - *Podľa kanála* sends no `kasaId` at all, which leaves the server to use the
            * channel's configured register and then its capability rule; that is what every close
            * does today. Not preselected to whichever the server would choose: showing a guess as a
            * choice makes somebody confirm a decision nobody made. */}
          <label className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground shrink-0">
              <T>Kasa</T>
            </span>
            <select
              value={kasaId}
              onChange={(event) => setKasaId(event.target.value)}
              aria-label={tt('Kasa')}
              data-pay-kasa
              className="flex-1 min-h-[36px] rounded-xl bg-card border border-border px-2 text-xs"
            >
              <option value="">{tt('Podľa kanála')}</option>
              {/* §10A.35 - every configured register, so a payment can be rung up on a third. */}
              {registerOptions.map((register) => (
                <option key={register.id} value={register.id}>
                  {register.label}
                </option>
              ))}
            </select>
          </label>

          {/* §9.369, FB-657 - a table paid at the till and left open for the food. The order says so
            * itself (`stampOrder` writes `fiskalPrintedAt`), so closing it later is offered here
            * rather than requiring a second payment the server would refuse with 409. */}
          {alreadyPaid(order) && (
            <div className="space-y-2" data-already-paid>
              <p className="text-xs text-muted-foreground">{alreadyPaidNote(order)}</p>
              <button
                type="button"
                data-close-paid-table
                disabled={isWorking || order.status === 'cancelled'}
                onClick={() => void closeTheTable(dueCents)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500 text-white font-bold text-sm hover:bg-emerald-400 transition-colors disabled:opacity-50"
              >
                {isWorking ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                <T>Zatvoriť stôl</T>
              </button>
            </div>
          )}

          <button
            type="button"
            // §9.367 - the whole bill prints and then asks about the table; a split pays its part
            // and leaves the table open by construction, so it needs no second question.
            onClick={() => void (isSplitting ? paySplit() : payAll())}
            disabled={
              isWorking ||
              order.status === 'cancelled' ||
              (isSplitting && dueCents === 0) ||
              waiterBlock !== null ||
              discountProblems(discount, dueCents).length > 0
            }
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500 text-white font-bold text-sm hover:bg-emerald-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isWorking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
            {isSplitting ? <T>Zaplatiť vybrané</T> : <T>Zaplatiť všetko</T>}
          </button>
        </div>
      </div>

      {/* §9.367, FB-655 - *Prebehla platba úspešne?* once the document is out, as legacy asks it
        * (`restaurant.tsx:3370`). Answering *no* leaves the table open and the order in the kitchen,
        * which is the owner's *"pay -> waiting for food"*. */}
      {askingToClose !== null &&
        ((question) => (
          <div
            role="dialog"
            aria-modal="true"
            aria-label={question.title}
            data-close-table-question
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          >
            <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
              <div>
                <h3 className="text-base font-bold text-foreground">{question.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{question.description}</p>
              </div>
              <button
                type="button"
                data-close-table-yes
                disabled={isWorking}
                onClick={() => void closeTheTable(askingToClose.paidCents)}
                className="w-full py-3 rounded-xl font-bold bg-emerald-500 text-white flex items-center justify-center gap-2 hover:bg-emerald-400 transition-colors disabled:opacity-50"
              >
                {isWorking ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {question.closeLabel}
              </button>
              <button
                type="button"
                data-close-table-keep-open
                disabled={isWorking}
                onClick={() => {
                  // **Nothing is called.** The document is out, the table stays open, and the
                  // kitchen keeps the order - the owner pressed cancel here expecting exactly this.
                  setAskingToClose(null)
                  onClose()
                }}
                className="w-full py-3 rounded-xl font-bold border border-border text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              >
                {question.keepOpenLabel}
              </button>
            </div>
          </div>
        ))(
          closeTableQuestion(
            // The order's number: `OrderListItem` carries no table name, and the dialog is
            // already open over the table the operator picked.
            `#${order.orderNumber ?? ''}`,
            formatCents(askingToClose.paidCents),
            { printed: askingToClose.printed },
          ),
        )}
    </div>
  )
}
